(function () {
  const VARIANT_SELECTS_SELECTOR = "variant-selects[data-section]";
  const PRODUCT_FORM_SELECTOR = '[data-type="add-to-cart-form"]';
  const PRODUCT_JSON_SELECTOR = "[data-product-json]";
  const SIZE_OPTION_FALLBACK = "size";
  const DEFAULT_SIZE_FALLBACK = "M";

  function normalizeValue(value) {
    return String(value || "").trim().toLowerCase();
  }

  function getPreferredSizes(section) {
    const rawValues = [
      section.dataset.customerLastBoughtSize,
      section.dataset.customerLastBoughtSizeNumber,
    ];

    const seen = new Set();
    return rawValues.reduce((preferredSizes, value) => {
      const trimmedValue = String(value || "").trim();
      const normalizedValue = normalizeValue(trimmedValue);
      if (!normalizedValue || seen.has(normalizedValue)) return preferredSizes;

      seen.add(normalizedValue);
      preferredSizes.push(trimmedValue);
      return preferredSizes;
    }, []);
  }

  function getDefaultSizes(section) {
    const rawValues = [section.dataset.defaultSize, section.dataset.defaultSizeShoes];
    const seen = new Set();

    return rawValues.reduce((defaultSizes, value) => {
      const trimmedValue = String(value || "").trim();
      const normalizedValue = normalizeValue(trimmedValue);
      if (!normalizedValue || seen.has(normalizedValue)) return defaultSizes;

      seen.add(normalizedValue);
      defaultSizes.push(trimmedValue);
      return defaultSizes;
    }, []);
  }

  function normalizeOptionLabel(value) {
    return String(value || "").replace(/\s*:\s*$/, "").trim();
  }

  function getOptionNamesFromPicker(variantSelects) {
    const optionNames = [];
    const seen = new Set();

    const fieldsets = variantSelects.querySelectorAll("fieldset");
    fieldsets.forEach((fieldset) => {
      const legend =
        fieldset.querySelector("legend") ||
        fieldset.querySelector(".form__label");
      const legendText = normalizeOptionLabel(legend ? legend.textContent : "");
      if (!legendText) return;

      const normalizedLegend = normalizeValue(legendText);
      if (seen.has(normalizedLegend)) return;
      seen.add(normalizedLegend);
      optionNames.push(legendText);
    });

    if (optionNames.length) return optionNames;

    const selectInputs = variantSelects.querySelectorAll("select[name^='options[']");
    selectInputs.forEach((selectInput) => {
      const match = selectInput.name.match(/^options\[(.*)\]$/);
      const optionName = normalizeOptionLabel(match && match[1] ? match[1] : "");
      if (!optionName) return;

      const normalizedOption = normalizeValue(optionName);
      if (seen.has(normalizedOption)) return;
      seen.add(normalizedOption);
      optionNames.push(optionName);
    });

    return optionNames;
  }

  function getProductData(variantSelects) {
    const productJsonElement =
      variantSelects.querySelector(PRODUCT_JSON_SELECTOR) ||
      variantSelects.querySelector('script[type="application/json"]');
    if (!productJsonElement) return null;

    try {
      const parsedData = JSON.parse(productJsonElement.textContent || "");
      const variants = Array.isArray(parsedData)
        ? parsedData
        : Array.isArray(parsedData.variants)
          ? parsedData.variants
          : [];
      const options = Array.isArray(parsedData && parsedData.options)
        ? parsedData.options
        : getOptionNamesFromPicker(variantSelects);

      if (!variants.length) return null;
      return { variants, options };
    } catch (error) {
      return null;
    }
  }

  function getCurrentVariant(form, productJson) {
    if (!productJson || !Array.isArray(productJson.variants)) return null;

    const idInput = form.querySelector('[name="id"]');
    const currentVariantId = Number(idInput && idInput.value);
    if (Number.isNaN(currentVariantId)) return null;

    return productJson.variants.find((variant) => variant.id === currentVariantId) || null;
  }

  function getSizeOptionNames(variantSelects) {
    const translatedNames = (variantSelects.dataset.sizeOptionLabels || "")
      .split(",")
      .map((value) => normalizeValue(value))
      .filter(Boolean);

    if (!translatedNames.includes(SIZE_OPTION_FALLBACK)) {
      translatedNames.push(SIZE_OPTION_FALLBACK);
    }

    return translatedNames;
  }

  function getSizeOptionIndex(variantSelects, productJson) {
    if (!productJson || !Array.isArray(productJson.options)) return -1;
    const sizeOptionNames = getSizeOptionNames(variantSelects);

    return productJson.options.findIndex((optionName) => {
      const normalizedOptionName = normalizeValue(optionName);
      return sizeOptionNames.some(
        (sizeOptionName) =>
          normalizedOptionName === sizeOptionName || normalizedOptionName.includes(sizeOptionName)
      );
    });
  }

  function findFirstAvailableSize(sizeValues, availableBySize) {
    if (!Array.isArray(sizeValues) || !sizeValues.length) return null;

    for (let index = 0; index < sizeValues.length; index += 1) {
      const sizeValue = sizeValues[index];
      if (availableBySize.has(normalizeValue(sizeValue))) {
        return sizeValue;
      }
    }

    return null;
  }

  function findSmallerAvailableSize(sizeValues, pivotSize, availableBySize) {
    if (!Array.isArray(sizeValues) || !sizeValues.length) return null;

    const normalizedPivot = normalizeValue(pivotSize);
    const pivotIndex = sizeValues.findIndex((size) => normalizeValue(size) === normalizedPivot);
    if (pivotIndex < 0) return null;

    for (let index = pivotIndex - 1; index >= 0; index -= 1) {
      const sizeValue = sizeValues[index];
      if (availableBySize.has(normalizeValue(sizeValue))) {
        return sizeValue;
      }
    }

    return null;
  }

  function pickSizeFromCustomerData(sizeValues, preferredSizes, availableBySize) {
    if (!Array.isArray(preferredSizes) || !preferredSizes.length) return null;

    for (let index = 0; index < preferredSizes.length; index += 1) {
      const preferredSize = preferredSizes[index];
      const normalizedPreferred = normalizeValue(preferredSize);
      if (!normalizedPreferred) continue;

      const matchedSize = sizeValues.find((size) => normalizeValue(size) === normalizedPreferred);
      if (!matchedSize) continue;

      if (availableBySize.has(normalizedPreferred)) {
        return matchedSize;
      }

      const smallerSize = findSmallerAvailableSize(sizeValues, matchedSize, availableBySize);
      if (smallerSize) return smallerSize;

      return findFirstAvailableSize(sizeValues, availableBySize);
    }

    return null;
  }

  function pickSizeFromDefaultRule(sizeValues, defaultSizes, availableBySize) {
    const firstAvailableSize = findFirstAvailableSize(sizeValues, availableBySize);
    const fallbackDefaultSizes = Array.isArray(defaultSizes) ? [...defaultSizes] : [];

    if (!fallbackDefaultSizes.length) {
      fallbackDefaultSizes.push(DEFAULT_SIZE_FALLBACK);
    }

    for (let index = 0; index < fallbackDefaultSizes.length; index += 1) {
      const defaultSize = fallbackDefaultSizes[index];
      const normalizedDefaultSize = normalizeValue(defaultSize);
      if (!normalizedDefaultSize) continue;

      const matchedDefaultSize = sizeValues.find(
        (sizeValue) => normalizeValue(sizeValue) === normalizedDefaultSize
      );
      if (!matchedDefaultSize) continue;

      if (availableBySize.has(normalizeValue(matchedDefaultSize))) {
        return matchedDefaultSize;
      }

      const smallerSize = findSmallerAvailableSize(sizeValues, matchedDefaultSize, availableBySize);
      if (smallerSize) return smallerSize;

      return firstAvailableSize;
    }

    return firstAvailableSize;
  }

  function getSizeValues(productJson, sizeOptionIndex) {
    const sizeValues = [];
    const seen = new Set();

    if (!productJson || !Array.isArray(productJson.variants)) return sizeValues;

    productJson.variants.forEach((variant) => {
      if (!variant || !Array.isArray(variant.options)) return;
      const value = variant.options[sizeOptionIndex];
      const normalizedValue = normalizeValue(value);
      if (!normalizedValue || seen.has(normalizedValue)) return;

      seen.add(normalizedValue);
      sizeValues.push(value);
    });

    return sizeValues;
  }

  function buildAvailableSizeMap(variants, sizeOptionIndex) {
    const availableBySize = new Map();

    variants.forEach((variant) => {
      if (!variant || !variant.available || !Array.isArray(variant.options)) return;
      const sizeValue = variant.options[sizeOptionIndex];
      const normalizedSize = normalizeValue(sizeValue);
      if (!normalizedSize || availableBySize.has(normalizedSize)) return;
      availableBySize.set(normalizedSize, variant);
    });

    return availableBySize;
  }

  function getVariantsForSelectionContext(productJson, currentVariant, sizeOptionIndex) {
    if (!productJson || !Array.isArray(productJson.variants)) return [];
    if (!currentVariant || !Array.isArray(currentVariant.options)) return productJson.variants;

    const narrowedVariants = productJson.variants.filter((variant) => {
      if (!variant || !Array.isArray(variant.options)) return false;

      return variant.options.every((optionValue, index) => {
        if (index === sizeOptionIndex) return true;
        return optionValue === currentVariant.options[index];
      });
    });

    if (!narrowedVariants.some((variant) => variant.available)) {
      return productJson.variants;
    }

    return narrowedVariants;
  }

  function getProductForm(variantSelects) {
    const connectedInputOrSelect = variantSelects.querySelector("input[form], select[form]");
    const formId = connectedInputOrSelect && connectedInputOrSelect.getAttribute("form");
    if (formId) {
      const formById = document.getElementById(formId);
      if (formById) return formById;
    }

    const sectionRoot = variantSelects.closest(".shopify-section, section, .product-section");
    if (!sectionRoot) return null;
    return sectionRoot.querySelector(PRODUCT_FORM_SELECTOR);
  }

  function triggerVariantChange(variantSelects, productJson, sizeOptionIndex, sizeValue) {
    const optionName = (productJson.options && productJson.options[sizeOptionIndex]) || "";
    const normalizedSize = normalizeValue(sizeValue);

    const radioInputs = variantSelects.querySelectorAll('input[type="radio"]');
    for (const input of radioInputs) {
      if (input.name !== optionName) continue;
      if (normalizeValue(input.value) !== normalizedSize) continue;

      if (!input.checked) {
        input.checked = true;
      }
      input.dispatchEvent(new Event("change", { bubbles: true }));
      return true;
    }

    const selects = variantSelects.querySelectorAll("select");
    for (const select of selects) {
      const expectedName = `options[${optionName}]`;
      if (select.name !== expectedName) continue;

      const matchingOption = Array.from(select.options).find(
        (option) => normalizeValue(option.value) === normalizedSize
      );
      if (!matchingOption) continue;

      select.value = matchingOption.value;
      select.dispatchEvent(new Event("change", { bubbles: true }));
      return true;
    }

    return false;
  }

  function applyDefaultSize(variantSelects) {
    if (variantSelects.dataset.pdpSizeDefaultApplied === "true") return;
    if (window.location.search.includes("variant=")) return;

    const form = getProductForm(variantSelects);
    if (!form) return;

    const productData = getProductData(variantSelects);
    if (!productData) return;

    const sizeOptionIndex = getSizeOptionIndex(variantSelects, productData);
    if (sizeOptionIndex < 0) return;

    const sizeValues = getSizeValues(productData, sizeOptionIndex);
    if (!sizeValues.length) return;

    const currentVariant = getCurrentVariant(form, productData);
    const candidateVariants = getVariantsForSelectionContext(productData, currentVariant, sizeOptionIndex);
    const availableBySize = buildAvailableSizeMap(candidateVariants, sizeOptionIndex);
    if (!availableBySize.size) return;

    const preferredSizes = getPreferredSizes(variantSelects);
    const defaultSizes = getDefaultSizes(variantSelects);

    const chosenSize =
      pickSizeFromCustomerData(sizeValues, preferredSizes, availableBySize) ||
      pickSizeFromDefaultRule(sizeValues, defaultSizes, availableBySize);
    if (!chosenSize) return;

    if (triggerVariantChange(variantSelects, productData, sizeOptionIndex, chosenSize)) {
      variantSelects.dataset.pdpSizeDefaultApplied = "true";
    }
  }

  function initPdpLastBoughtSizeDefaults() {
    const variantSelectsNodes = document.querySelectorAll(VARIANT_SELECTS_SELECTOR);
    variantSelectsNodes.forEach((variantSelects) => {
      const hasLastSizeData =
        normalizeValue(variantSelects.dataset.customerLastBoughtSize) ||
        normalizeValue(variantSelects.dataset.customerLastBoughtSizeNumber) ||
        normalizeValue(variantSelects.dataset.defaultSize);
      if (!hasLastSizeData) return;

      applyDefaultSize(variantSelects);
    });
  }

  document.addEventListener("DOMContentLoaded", initPdpLastBoughtSizeDefaults);
  document.addEventListener("shopify:section:load", initPdpLastBoughtSizeDefaults);
})();